// ✅ src/pages/VehicleV2/DayWiseLubeOutwardReport_Print.jsx
// React PRINT page for: DayWiseLubeOutwardReportPrint.aspx (Crystal)
// - Reads params from BOTH querystring + navigation state (same as your VehiclewiseReport_Print template)
// - Calls backend:
//    GET /api/daywiseLubeOutwardReport/list
//      params: branchId, monthValue, monthText, year, itemCategoryId, itemId, page=1, pageSize=5000
//
// Expected mount (backend):
//   app.use("/api/daywiseLubeOutwardReport", daywiseLubeOutwardReport);

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "http://nissi-india.com/api/daywiseLubeOutwardReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

const clean = (v) => (isSelect(v) ? "" : toStr(v));

const buildHeaders = () => {
  // adjust keys as per your project
  const finYearId =
    localStorage.getItem("finYearId") ||
    localStorage.getItem("x-finyearid") ||
    sessionStorage.getItem("finYearId") ||
    "";
  const finYear =
    localStorage.getItem("finYear") ||
    localStorage.getItem("x-finyear") ||
    sessionStorage.getItem("finYear") ||
    "";

  const branchType =
    sessionStorage.getItem("branchtype") ||
    localStorage.getItem("branchtype") ||
    localStorage.getItem("x-branchtype") ||
    "";

  const branchId =
    sessionStorage.getItem("branchid") ||
    localStorage.getItem("branchid") ||
    localStorage.getItem("x-branchid") ||
    "";

  const h = {};
  if (finYearId) h["x-finyearid"] = finYearId;
  if (finYear) h["x-finyear"] = finYear;
  if (branchType) h["x-branchtype"] = branchType;
  if (branchId) h["x-branchid"] = branchId;
  return h;
};

// MonthValue -> MonthText
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const monthTextFromValue = (mv) => {
  const n = Number(mv);
  if (!Number.isFinite(n) || n < 1 || n > 12) return "";
  return MONTHS[n - 1];
};

export default function DayWiseLubeOutwardReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ support BOTH state + querystring
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const passed = location?.state || {};

  // ✅ Build params (querystring first, then state fallback)
  const filters = useMemo(() => {
    // querystring keys (recommend these)
    const q = {
      branchId: toStr(qs.get("branchId") || qs.get("branch") || qs.get("fld_branchid")),
      monthValue: toStr(qs.get("monthValue") || qs.get("month") || qs.get("fld_monthid")),
      monthText: toStr(qs.get("monthText")),
      year: toStr(qs.get("year")),
      itemCategoryId: toStr(qs.get("itemCategoryId") || qs.get("fld_itemcategory")),
      itemId: toStr(qs.get("itemId") || qs.get("fld_itemid")),
      itemNameText: toStr(qs.get("itemNameText") || qs.get("fld_itemname")),
    };

    const state = {
      branchId: pick(passed, ["branchId", "branch", "fld_branchid"], ""),
      monthValue: pick(passed, ["monthValue", "month", "ddl_month", "monthId"], ""),
      monthText: pick(passed, ["monthText", "monthname", "monthName"], ""),
      year: pick(passed, ["year"], ""),
      itemCategoryId: pick(passed, ["itemCategoryId", "itemCategory", "fld_itemcategory"], "37"),
      itemId: pick(passed, ["itemId", "itemName", "fld_itemid"], "Select"),
      itemNameText: pick(passed, ["itemNameText", "fld_itemname", "itemNameLabel"], ""),
    };

    const mv = clean(q.monthValue) || clean(state.monthValue);
    const mt =
      clean(q.monthText) ||
      clean(state.monthText) ||
      monthTextFromValue(mv);

    const finalParams = {
      branchId: clean(q.branchId) || clean(state.branchId),
      monthValue: mv,
      monthText: mt,
      year: clean(q.year) || clean(state.year),
      itemCategoryId: clean(q.itemCategoryId) || clean(state.itemCategoryId) || "37",
      itemId: clean(q.itemId) || clean(state.itemId) || "Select",

      // display only
      itemNameText: clean(q.itemNameText) || clean(state.itemNameText),
      branchType: toStr(sessionStorage.getItem("branchtype") || passed?.branchType || ""),
    };

    return finalParams;
  }, [qs, passed]);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totalDays, setTotalDays] = useState(0);

  const hasRequired = useMemo(() => {
    return !!filters.branchId && !!filters.monthText && !!filters.year;
  }, [filters]);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      if (!hasRequired) {
        throw new Error("Print params missing. Please open Print from DayWise Lube Outward Report screen.");
      }

      const headers = buildHeaders();

      // ✅ fetch ALL rows for print
      const { data } = await axios.get(`${API_BASE}/list`, {
        headers,
        params: {
          branchId: filters.branchId,
          monthValue: filters.monthValue,
          monthText: filters.monthText,
          year: filters.year,
          itemCategoryId: filters.itemCategoryId,
          itemId: filters.itemId || "Select",
          page: 1,
          pageSize: 5000,
        },
      });

      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setColumns(Array.isArray(data?.columns) ? data.columns : []);
      setTotalDays(Number(data?.totaldays || 0));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      setColumns([]);
      setTotalDays(0);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    
      <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
        {/* Optional header bar (can be removed if you want pure-print page) */}
       

        <div className="w-full bg-white">
          <style>{`
            @page { size: A4 landscape; margin: 8mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          {/* Top actions */}
          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading ? "Loading..." : errMsg ? <span className="text-red-600">{errMsg}</span> : `Records: ${rows.length}`}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>

              <button
                onClick={() => window.print()}
                disabled={loading}
                className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>
            </div>
          </div>

          {/* Print body */}
          <div className="print-area mx-auto max-w-[1100px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                {/* Header */}
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>

                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="border-b border-black text-center font-bold py-2 text-sm">
                  DAY WISE LUBE OUTWARD REPORT
                </div>

                {/* Filter info */}
                <div className="border-b border-black p-2 text-[12px]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div>
                        <span className="font-semibold">Branch</span> : {filters.branchId || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Month</span> : {filters.monthText || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Year</span> : {filters.year || "-"}
                      </div>
                    </div>

                    <div className="text-right">
                      <div>
                        <span className="font-semibold">Item Category</span> : {filters.itemCategoryId || "37"}
                      </div>
                      <div>
                        <span className="font-semibold">Item</span> : {filters.itemNameText || filters.itemId || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Total Days</span> : {totalDays || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table (dynamic columns like SP output / Crystal day columns) */}
                <div className="p-2">
                  <div className="overflow-x-auto">
                    <table className="w-full border border-black border-collapse text-[11px]">
                      <thead>
                        <tr>
                          {/* S.No */}
                          <th className="border border-black px-2 py-1 w-[50px]">S.No</th>

                          {(columns || []).map((c) => (
                            <th key={c} className="border border-black px-2 py-1 whitespace-nowrap">
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {!loading && rows.length === 0 ? (
                          <tr>
                            <td className="border border-black px-2 py-2 text-center" colSpan={(columns?.length || 0) + 1}>
                              No records found.
                            </td>
                          </tr>
                        ) : (
                          rows.map((r, idx) => (
                            <tr key={idx}>
                              <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>

                              {(columns || []).map((c) => (
                                <td key={c} className="border border-black px-2 py-1 text-center whitespace-nowrap">
                                  {r?.[c] ?? ""}
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>

            {/* Debug */}
            <div className="no-print mt-4 text-xs text-gray-600">
              <details>
                <summary className="cursor-pointer">Debug: Query + Filters</summary>
                <pre className="mt-2 p-3 border rounded bg-gray-50 overflow-auto">
                  {JSON.stringify(
                    {
                      urlSearch: location.search,
                      filters,
                      rowsCount: rows.length,
                      columnsCount: columns.length,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>
            </div>
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
   
  );
}